import { useState, useRef, useEffect } from "react";
import { motion, useInView } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  ShieldCheck,
  Coins,
  Landmark,
  ArrowRight,
  Check,
  Quote,
  Menu,
  X,
  BadgeCheck,
  TrendingUp,
  Users,
  Briefcase,
  Scale,
  Home,
  Linkedin,
  CalendarClock,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// NOTA: sostituisci con il tuo URL LinkedIn reale
const LINKEDIN_URL = "https://www.linkedin.com/in/alassane-diallo-46031a1ba";

const IMG = {
  gold: "https://images.pexels.com/photos/321452/pexels-photo-321452.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=900",
  vault: "https://images.unsplash.com/photo-1582139329536-e7284fece509?auto=format&fit=crop&w=1200&q=80",
  portrait: "https://customer-assets.emergentagent.com/job_ig-bio-builder/artifacts/2ekyvd9s_WhatsApp%20Image%202026-07-07%20at%2020.36.53.jpeg",
  goldVertical: "https://static.prod-images.emergentagent.com/jobs/b3b7813c-cf8b-478c-928a-0272799d3181/images/1f5ae81002d4ea873adda3758c8cace6b83a2d352528bddd0556ec1203427568.png",
};

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

const Reveal = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const Overline = ({ children }) => (
  <span className="overline block mb-4" data-testid="section-overline">{children}</span>
);

// === Header ===
const Header = () => {
  const [open, setOpen] = useState(false);
  const links = [
    { label: "Chi sono", id: "chi-sono" },
    { label: "Per chi lavoro", id: "per-chi" },
    { label: "Il Metodo", id: "metodo" },
    { label: "Commercialisti", id: "commercialisti" },
    { label: "Contatti", id: "contatti" },
  ];
  return (
    <header className="fixed top-0 inset-x-0 z-50 glass border-b border-yellow-500/10" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <button onClick={() => scrollTo("hero")} className="flex items-center gap-2.5" data-testid="logo-button">
          <Coins className="text-[#D4AF37]" strokeWidth={1.5} size={24} />
          <span className="font-serif text-xl md:text-2xl font-semibold tracking-tight text-white">
            Alassane <span style={{ color: "#D4AF37" }}>Diallo</span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => scrollTo(l.id)}
              className="text-sm text-zinc-400 hover:text-[#D4AF37] transition-colors duration-300"
              data-testid={`nav-${l.id}`}
            >
              {l.label}
            </button>
          ))}
          <a
            href="/piano-azione"
            className="text-sm text-[#D4AF37] hover:text-[#F3E5AB] transition-colors duration-300 font-medium"
            data-testid="nav-calcolatore"
          >
            Piano Azione
          </a>
          <button
            onClick={() => scrollTo("contatti")}
            className="px-5 py-2.5 rounded-full bg-[#D4AF37] text-black text-sm font-semibold hover:bg-[#F3E5AB] hover:-translate-y-0.5 transition-all duration-300"
            data-testid="header-cta"
          >
            Prenota una call
          </button>
        </nav>

        <button className="lg:hidden text-[#D4AF37]" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden glass border-t border-yellow-500/10 px-6 py-6 flex flex-col gap-4" data-testid="mobile-menu">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => { scrollTo(l.id); setOpen(false); }}
              className="text-left text-zinc-300 hover:text-[#D4AF37] transition-colors"
              data-testid={`mobile-nav-${l.id}`}
            >
              {l.label}
            </button>
          ))}
          <a
            href="/piano-azione"
            onClick={() => setOpen(false)}
            className="text-left text-[#D4AF37] hover:text-[#F3E5AB] transition-colors font-medium"
            data-testid="mobile-nav-calcolatore"
          >
            Piano Azione
          </a>
          <button
            onClick={() => { scrollTo("contatti"); setOpen(false); }}
            className="mt-2 px-5 py-3 rounded-full bg-[#D4AF37] text-black font-semibold"
            data-testid="mobile-header-cta"
          >
            Prenota una call
          </button>
        </div>
      )}
    </header>
  );
};

// === Hero ===
const Hero = () => (
  <section id="hero" className="relative min-h-screen flex items-center pt-28 pb-16 overflow-hidden grain">
    <div className="absolute inset-0 z-0">
      <div className="absolute right-0 top-0 h-full w-full md:w-[52%]">
        <img src={IMG.gold} alt="Lingotti d'oro" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0B] via-[#0A0A0B]/75 to-transparent" />
        <div className="absolute inset-0 bg-[#0A0A0B]/45 md:hidden" />
      </div>
    </div>

    <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full">
      <div className="max-w-2xl">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <Overline>Consulente in Protezione Patrimoniale</Overline>
        </motion.div>
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="font-serif text-4xl md:text-5xl lg:text-[3.5rem] leading-[1.12] tracking-tight text-white"
          data-testid="hero-title"
        >
          Aiuto imprenditori e professionisti a proteggere il patrimonio con <span style={{ color: "#D4AF37" }}>l'oro fisico</span>.
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.25 }}
          className="mt-7 text-lg text-zinc-300 leading-relaxed max-w-xl"
          data-testid="hero-subtitle"
        >
          Consulente in Protezione Patrimoniale. Collaboro con operatori OAM autorizzati
          per offrire una strategia di diversificazione fuori dal sistema bancario tradizionale.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <button
            onClick={() => scrollTo("contatti")}
            className="group px-8 py-4 rounded-full bg-[#D4AF37] text-black font-semibold flex items-center justify-center gap-2 hover:bg-[#F3E5AB] hover:-translate-y-0.5 transition-all duration-300 shadow-[0_0_40px_-8px_rgba(212,175,55,0.5)]"
            data-testid="hero-cta-call"
          >
            <CalendarClock size={18} /> Prenota una call conoscitiva
          </button>
          <a
            href={LINKEDIN_URL}
            target="_blank"
            rel="noreferrer"
            className="px-8 py-4 rounded-full border border-yellow-500/40 text-[#F3E5AB] font-semibold glass hover:border-yellow-500/80 hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center gap-2"
            data-testid="hero-cta-linkedin"
          >
            <Linkedin size={18} /> Scrivimi su LinkedIn
          </a>
        </motion.div>
      </div>
    </div>
  </section>
);

// === Stats ===
const stats = [
  { icon: CalendarClock, value: "10+", label: "Anni di esperienza" },
  { icon: Users, value: "150+", label: "Clienti seguiti" },
  { icon: BadgeCheck, value: "OAM", label: "Operatori autorizzati" },
  { icon: Landmark, value: "100%", label: "Fuori dal sistema bancario" },
];

const Stats = () => (
  <section id="risultati" className="py-20 px-6 md:px-12 border-t border-yellow-500/10">
    <div className="max-w-7xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-5">
      {stats.map((s, i) => (
        <Reveal key={i} delay={i * 0.08}>
          <div className="glass rounded-2xl border border-yellow-500/15 p-7 h-full hover:border-yellow-500/40 hover:-translate-y-1 transition-all duration-400" data-testid={`stat-card-${i}`}>
            <s.icon className="text-[#D4AF37] mb-5" strokeWidth={1.5} size={26} />
            <div className="font-serif text-4xl md:text-5xl text-white">{s.value}</div>
            <div className="mt-2 text-zinc-400 text-sm">{s.label}</div>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

// === Chi sono ===
const ChiSono = () => (
  <section id="chi-sono" className="py-24 px-6 md:px-12 border-t border-yellow-500/10">
    <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
      <Reveal>
        <div className="relative rounded-3xl overflow-hidden">
          <img src={IMG.portrait} alt="Alassane Diallo — Consulente Protezione Patrimoniale" className="w-full h-[600px] object-cover object-top" data-testid="about-image" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B]/50 via-transparent to-transparent" />
        </div>
      </Reveal>

      <div>
        <Reveal>
          <Overline>Chi sono</Overline>
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight text-white" data-testid="about-title">
            Alassane Diallo
          </h2>
          <p className="text-[#D4AF37] mt-2 font-medium">Consulente strategico in Protezione Patrimoniale</p>
          <p className="mt-6 text-zinc-300 leading-relaxed">
            Da 10 anni lavoro nel settore della protezione patrimoniale attraverso l'oro fisico.
            Ho affiancato oltre 150 imprenditori, professionisti e famiglie nella costruzione di una
            strategia patrimoniale solida, sempre in collaborazione con operatori OAM autorizzati.
          </p>
          <p className="mt-4 text-zinc-400 leading-relaxed">
            Il mio ruolo è quello di consulente strategico: analizzo la situazione patrimoniale,
            individuo le criticità e propongo soluzioni concrete — senza vendere prodotti finanziari,
            ma indirizzando verso professionisti regolamentati del settore.
          </p>
        </Reveal>

        <div className="mt-8 grid grid-cols-3 gap-4">
          {[
            { v: "10+", l: "Anni di esperienza" },
            { v: "150+", l: "Clienti seguiti" },
            { v: "OAM", l: "Operatori autorizzati" },
          ].map((s, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div className="glass rounded-2xl border border-yellow-500/15 p-5 text-center h-full" data-testid={`about-stat-${i}`}>
                <div className="font-serif text-2xl text-[#D4AF37]">{s.v}</div>
                <div className="mt-1 text-xs text-zinc-500">{s.l}</div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.3}>
          <button
            onClick={() => scrollTo("contatti")}
            className="mt-9 px-8 py-4 rounded-full bg-[#D4AF37] text-black font-semibold inline-flex items-center gap-2 hover:bg-[#F3E5AB] hover:-translate-y-0.5 transition-all duration-300"
            data-testid="about-cta"
          >
            Prenota una call conoscitiva <ArrowRight size={18} />
          </button>
        </Reveal>
      </div>
    </div>
  </section>
);

// === Per chi lavoro ===
const audiences = [
  { icon: Briefcase, title: "Imprenditori con SRL", desc: "Chi vuole proteggere il patrimonio personale dall'esposizione legata all'attività d'impresa." },
  { icon: Scale, title: "Liberi professionisti", desc: "Commercialisti, avvocati e medici che cercano una diversificazione solida e concreta." },
  { icon: Home, title: "Famiglie", desc: "Chi vuole custodire e trasmettere il proprio patrimonio con un bene reale e tangibile." },
];

const PerChiLavoro = () => (
  <section id="per-chi" className="py-24 px-6 md:px-12 border-t border-yellow-500/10">
    <div className="max-w-7xl mx-auto">
      <Reveal>
        <Overline>Per chi lavoro</Overline>
        <h2 className="font-serif text-3xl md:text-4xl tracking-tight text-white max-w-3xl" data-testid="audience-title">
          A chi mi rivolgo
        </h2>
        <p className="mt-5 text-zinc-400 max-w-3xl leading-relaxed">
          Imprenditori con SRL, liberi professionisti (commercialisti, avvocati, medici) e famiglie
          che vogliono diversificare il patrimonio con un bene reale, tangibile, fuori dalla
          volatilità dei mercati finanziari tradizionali.
        </p>
      </Reveal>
      <div className="mt-12 grid md:grid-cols-3 gap-6">
        {audiences.map((a, i) => (
          <Reveal key={i} delay={i * 0.1}>
            <div className="glass rounded-2xl border border-yellow-500/15 p-8 h-full hover:border-yellow-500/40 hover:-translate-y-1 transition-all duration-400" data-testid={`audience-card-${i}`}>
              <a.icon className="text-[#D4AF37] mb-5" strokeWidth={1.5} size={30} />
              <h3 className="font-serif text-2xl text-white">{a.title}</h3>
              <p className="mt-3 text-zinc-400 leading-relaxed">{a.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

// === Il Metodo ===
const steps = [
  { n: "01", title: "Analisi patrimoniale", desc: "Analizzo la tua situazione patrimoniale e individuo le criticità." },
  { n: "02", title: "Strategia su misura", desc: "Definiamo insieme una strategia di diversificazione basata sull'oro fisico." },
  { n: "03", title: "Professionisti regolamentati", desc: "Ti indirizzo verso operatori OAM autorizzati, senza vendere prodotti finanziari." },
];

const Metodo = () => (
  <section id="metodo" className="py-24 px-6 md:px-12 border-t border-yellow-500/10">
    <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
      <Reveal>
        <div className="relative rounded-3xl overflow-hidden">
          <img src={IMG.vault} alt="Caveau di sicurezza" className="w-full h-[520px] object-cover" data-testid="method-image" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B] via-transparent to-transparent" />
          <div className="absolute bottom-6 left-6 glass rounded-xl px-5 py-4 border border-yellow-500/20">
            <div className="overline">Fuori dal sistema bancario</div>
            <div className="font-serif text-2xl mt-1 text-white">Un bene reale e tangibile</div>
          </div>
        </div>
      </Reveal>

      <div>
        <Reveal>
          <Overline>Il Metodo</Overline>
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight text-white" data-testid="method-title">
            Come lavoro, passo dopo passo
          </h2>
        </Reveal>
        <div className="mt-10 space-y-8">
          {steps.map((s, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div className="flex gap-6" data-testid={`method-step-${i}`}>
                <div className="font-serif text-3xl text-[#D4AF37] shrink-0 w-14">{s.n}</div>
                <div>
                  <h3 className="font-serif text-2xl text-white">{s.title}</h3>
                  <p className="mt-2 text-zinc-400 leading-relaxed">{s.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

// === Perché oro fisico ===
const benefits = [
  { icon: Coins, title: "Bene reale e tangibile", desc: "L'oro fisico è un asset concreto, non un prodotto finanziario astratto." },
  { icon: ShieldCheck, title: "Fuori dal sistema bancario", desc: "Un patrimonio che non dipende dalla solidità del sistema bancario." },
  { icon: TrendingUp, title: "Diversificazione", desc: "Riduce l'esposizione alla volatilità dei mercati finanziari tradizionali." },
];

const Benefici = () => (
  <section className="py-24 px-6 md:px-12 border-t border-yellow-500/10">
    <div className="max-w-7xl mx-auto">
      <Reveal>
        <Overline>Perché l'oro fisico</Overline>
        <h2 className="font-serif text-3xl md:text-4xl tracking-tight text-white max-w-2xl" data-testid="benefits-title">
          Un bene reale per diversificare il patrimonio
        </h2>
      </Reveal>
      <div className="mt-12 grid md:grid-cols-3 gap-6">
        {benefits.map((b, i) => (
          <Reveal key={i} delay={i * 0.1}>
            <div className="glass rounded-2xl border border-yellow-500/15 p-8 h-full hover:border-yellow-500/40 hover:-translate-y-1 transition-all duration-400" data-testid={`benefit-card-${i}`}>
              <b.icon className="text-[#D4AF37] mb-5" strokeWidth={1.5} size={30} />
              <h3 className="font-serif text-2xl text-white">{b.title}</h3>
              <p className="mt-3 text-zinc-400 leading-relaxed">{b.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

// === Testimonianze ===
const testimonials = [
  { quote: "Alex si è dimostrato estremamente serio e professionale. La sua profonda conoscenza degli investimenti in oro offre una prospettiva solida e sicura: un'opportunità concreta per proteggere e far crescere i propri risparmi. Lo consiglio vivamente a chi cerca un investimento stabile e di lungo termine.", name: "Marco", role: "Imprenditore con SRL" },
  { quote: "Sempre disponibile e professionale, si distingue per gentilezza e affidabilità: lavora con impegno e passione. Consigliato per chi cerca qualità e serietà.", name: "Roberto", role: "Libero professionista" },
  { quote: "Si capisce subito che sei un professionista. Competenza e serietà fuori dal comune.", name: "Alessandro", role: "Cliente" },
];

const Testimonials = () => (
  <section className="py-24 px-6 md:px-12 border-t border-yellow-500/10">
    <div className="max-w-7xl mx-auto">
      <Reveal>
        <Overline>Testimonianze</Overline>
        <h2 className="font-serif text-3xl md:text-4xl tracking-tight text-white" data-testid="testimonials-title">
          La parola ai miei clienti
        </h2>
      </Reveal>
      <div className="mt-12 grid md:grid-cols-3 gap-6">
        {testimonials.map((t, i) => (
          <Reveal key={i} delay={i * 0.1}>
            <div className="relative pl-6 border-l-2 border-[#D4AF37] py-2 h-full" data-testid={`testimonial-${i}`}>
              <Quote className="text-[#D4AF37]/40 mb-4" size={30} />
              <p className="font-serif text-xl italic leading-relaxed text-zinc-200">"{t.quote}"</p>
              <div className="mt-6">
                <div className="font-semibold text-[#F3E5AB]">{t.name}</div>
                <div className="text-sm text-zinc-500">{t.role}</div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

// === Per i commercialisti (partnership) ===
const Commercialisti = ({ onApply }) => (
  <section id="commercialisti" className="py-24 px-6 md:px-12 border-t border-yellow-500/10">
    <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
      <div>
        <Reveal>
          <Overline>Per i commercialisti e i professionisti</Overline>
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight text-white" data-testid="partner-title">
            Un servizio in più per i tuoi clienti
          </h2>
          <p className="mt-6 text-zinc-300 leading-relaxed">
            Collaboro anche con commercialisti e consulenti che vogliono offrire ai propri clienti
            un servizio aggiuntivo di protezione patrimoniale, senza uscire dal proprio ruolo professionale.
          </p>
          <p className="mt-4 text-zinc-400 leading-relaxed">
            Se sei un commercialista o un professionista e vuoi saperne di più su questa collaborazione,
            candidati. Se selezionato, lavorerai 1-1 con me e tutto il nostro board e avrai accesso
            a tutta la nostra formazione interna.
          </p>
        </Reveal>
        <Reveal delay={0.2}>
          <button
            onClick={onApply}
            className="mt-9 px-8 py-4 rounded-full bg-[#D4AF37] text-black font-semibold inline-flex items-center gap-2 hover:bg-[#F3E5AB] hover:-translate-y-0.5 transition-all duration-300"
            data-testid="partner-cta"
          >
            Candidati — scrivi "MAPPA" <ArrowRight size={18} />
          </button>
        </Reveal>
      </div>

      <Reveal>
        <div className="glass rounded-3xl border border-yellow-500/20 p-8 md:p-10">
          <div className="overline mb-6">Cosa ottieni con la collaborazione</div>
          <div className="space-y-5">
            {[
              "Percorso 1-1 con me e con tutto il board",
              "Accesso completo alla nostra formazione interna",
              "Un servizio aggiuntivo per i tuoi clienti, restando nel tuo ruolo",
              "Collaborazione con operatori OAM autorizzati",
            ].map((p, i) => (
              <div key={i} className="flex items-start gap-3" data-testid={`partner-point-${i}`}>
                <div className="w-6 h-6 rounded-full bg-[#D4AF37]/15 flex items-center justify-center shrink-0 mt-0.5">
                  <Check size={14} className="text-[#D4AF37]" />
                </div>
                <span className="text-zinc-300">{p}</span>
              </div>
            ))}
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

// === Lead Form ===
const LeadForm = ({ init }) => {
  const [form, setForm] = useState({ nome: "", email: "", telefono: "", tipo: init.tipo, messaggio: init.messaggio, website: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [consenso, setConsenso] = useState(false);

  useEffect(() => {
    setForm((f) => ({ ...f, tipo: init.tipo, messaggio: init.messaggio }));
  }, [init]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome || !form.email || !form.telefono) {
      toast.error("Compila tutti i campi obbligatori.");
      return;
    }
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email);
    if (!emailOk) {
      toast.error("Inserisci un indirizzo email valido.");
      return;
    }
    const phoneDigits = (form.telefono.match(/\d/g) || []).length;
    if (phoneDigits < 7) {
      toast.error("Inserisci un numero di telefono valido.");
      return;
    }
    if (!consenso) {
      toast.error("Devi accettare l'informativa privacy per continuare.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      setDone(true);
      toast.success("Richiesta inviata! Ti ricontatto a breve.");
      setForm({ nome: "", email: "", telefono: "", tipo: init.tipo, messaggio: "", website: "" });
      setConsenso(false);
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = typeof d === "string" ? d : (d?.[0]?.msg || "Errore nell'invio. Riprova.");
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-black/40 border border-yellow-500/20 rounded-xl px-4 py-3.5 text-zinc-100 placeholder-zinc-500 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]/50 transition-all duration-300";

  return (
    <section id="contatti" className="py-24 px-6 md:px-12 border-t border-yellow-500/10 grain relative">
      <div className="max-w-3xl mx-auto relative z-10">
        <Reveal className="text-center">
          <Overline>Parliamone</Overline>
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight text-white" data-testid="form-title">
            Prenota una call conoscitiva
          </h2>
          <p className="mt-5 text-zinc-400 max-w-xl mx-auto">
            Lascia i tuoi dati: ti ricontatto per fissare una call conoscitiva di 20 minuti,
            senza impegno, per capire come posso esserti utile.
          </p>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-12 glass rounded-3xl border border-yellow-500/20 p-8 md:p-10">
            {done ? (
              <div className="text-center py-10" data-testid="form-success">
                <div className="w-16 h-16 rounded-full bg-[#D4AF37]/15 flex items-center justify-center mx-auto mb-6">
                  <Check className="text-[#D4AF37]" size={32} />
                </div>
                <h3 className="font-serif text-3xl text-white">Richiesta ricevuta</h3>
                <p className="mt-3 text-zinc-400 max-w-md mx-auto">Grazie! Ti ricontatterò personalmente a breve per fissare la call conoscitiva.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="lead-form">
                <input
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.website}
                  onChange={(e) => update("website", e.target.value)}
                  style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
                  aria-hidden="true"
                  data-testid="honeypot-field"
                />
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">Nome e cognome *</label>
                    <input className={inputCls} value={form.nome} onChange={(e) => update("nome", e.target.value)}
                      placeholder="Mario Rossi" data-testid="input-nome" />
                  </div>
                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">Telefono *</label>
                    <input className={inputCls} value={form.telefono} onChange={(e) => update("telefono", e.target.value)}
                      placeholder="+39 333 1234567" data-testid="input-telefono" />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Email *</label>
                  <input type="email" className={inputCls} value={form.email} onChange={(e) => update("email", e.target.value)}
                    placeholder="mario@azienda.it" data-testid="input-email" />
                </div>

                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Come posso aiutarti? *</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                      { v: "cliente", l: "Voglio proteggere il mio patrimonio" },
                      { v: "talento", l: "Sono un commercialista / professionista" },
                    ].map((o) => (
                      <button type="button" key={o.v} onClick={() => update("tipo", o.v)}
                        className={`px-4 py-3.5 rounded-xl border text-sm font-medium transition-all duration-300 ${
                          form.tipo === o.v
                            ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#F3E5AB]"
                            : "border-yellow-500/20 text-zinc-400 hover:border-yellow-500/40"
                        }`}
                        data-testid={`tipo-${o.v}`}>
                        {o.l}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Messaggio (opzionale)</label>
                  <textarea rows={3} className={inputCls} value={form.messaggio} onChange={(e) => update("messaggio", e.target.value)}
                    placeholder="Raccontami brevemente la tua situazione..." data-testid="input-messaggio" />
                </div>

                <label className="flex items-start gap-2.5 text-xs text-zinc-500 leading-relaxed cursor-pointer select-none" data-testid="consenso-label">
                  <input type="checkbox" checked={consenso} onChange={(e) => setConsenso(e.target.checked)}
                    className="mt-0.5 h-4 w-4 shrink-0" style={{ accentColor: "#D4AF37" }} data-testid="input-consenso" />
                  <span>
                    Ho letto e accetto l'
                    <a href="/privacy" target="_blank" rel="noreferrer" className="underline text-[#D4AF37] hover:opacity-80" data-testid="privacy-link">informativa sulla privacy</a>
                    {" "}e acconsento al trattamento dei miei dati per essere ricontattato. I tuoi dati sono al sicuro. Nessuno spam.
                  </span>
                </label>
                <button type="submit" disabled={loading}
                  className="w-full px-8 py-4 rounded-full bg-[#D4AF37] text-black font-semibold flex items-center justify-center gap-2 hover:bg-[#F3E5AB] hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed shadow-[0_0_40px_-8px_rgba(212,175,55,0.5)]"
                  data-testid="submit-button">
                  {loading ? "Invio in corso..." : "Invia la richiesta"}
                  {!loading && <ArrowRight size={18} />}
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

// === Footer ===
const Footer = () => (
  <footer className="py-12 px-6 md:px-12 border-t border-yellow-500/10">
    <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
      <div className="flex items-center gap-2">
        <Coins className="text-[#D4AF37]" strokeWidth={1.5} size={22} />
        <span className="font-serif text-xl font-semibold text-white">Alassane <span style={{ color: "#D4AF37" }}>Diallo</span></span>
      </div>
      <p className="text-sm text-zinc-500 text-center">
        Consulente in Protezione Patrimoniale · Oro Fisico · Operatori OAM autorizzati
      </p>
      <div className="flex items-center gap-6">
        <a href="/privacy" className="text-sm text-zinc-400 hover:text-[#D4AF37] transition-colors" data-testid="footer-privacy">
          Privacy
        </a>
        <a href={LINKEDIN_URL} target="_blank" rel="noreferrer" className="text-sm text-zinc-400 hover:text-[#D4AF37] transition-colors flex items-center gap-2" data-testid="footer-linkedin">
          <Linkedin size={16} /> LinkedIn
        </a>
      </div>
    </div>
  </footer>
);

// === Page ===
export default function LandingPage() {
  const [formInit, setFormInit] = useState({ tipo: "cliente", messaggio: "" });

  const applyAsPartner = () => {
    setFormInit({ tipo: "talento", messaggio: "MAPPA — Sono un commercialista/professionista interessato alla collaborazione." });
    scrollTo("contatti");
  };

  return (
    <div className="bg-[#0A0A0B] text-[#FAFAFA] overflow-x-hidden">
      <Header />
      <Hero />
      <Stats />
      <ChiSono />
      <PerChiLavoro />
      <Metodo />
      <Benefici />
      <Testimonials />
      <Commercialisti onApply={applyAsPartner} />
      <LeadForm init={formInit} />
      <Footer />
    </div>
  );
}
